import React from "react";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import Zoom from "@mui/material/Zoom";
import { alpha, useTheme } from "@mui/material/styles";

import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import AutorenewRoundedIcon from "@mui/icons-material/AutorenewRounded";
import EventBusyRoundedIcon from "@mui/icons-material/EventBusyRounded";

const ZoomTransition = React.forwardRef(function ZoomTransition(props, ref) {
  return <Zoom ref={ref} {...props} />;
});

const formatDate = (value) => {
  if (!value) return null;
  try {
    return new Intl.DateTimeFormat("pt-BR", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    }).format(new Date(value));
  } catch (e) {
    return null;
  }
};

/**
 * Subscription-expiry alert rendered as a centered modal (replaces the old
 * top Snackbar bar). Presentational only — the parent manager owns the fetch
 * and the per-user/per-cycle dismissal logic.
 */
export default function SubscriptionExpiryModal({
  open,
  alertData,
  onRenew,
  onClose,
}) {
  const theme = useTheme();

  if (!alertData) {
    return null;
  }

  const inGrace = alertData.in_grace_period;
  const days = alertData.days_left;
  const planName = alertData.plan_name;
  const expiresLabel = formatDate(alertData.expires_at);

  // error tone for grace period or the final day, warning tone otherwise.
  const severity = inGrace || days <= 1 ? "error" : "warning";
  const accent = theme.palette[severity].main;
  const HeaderIcon = inGrace ? ErrorOutlineRoundedIcon : WarningAmberRoundedIcon;

  let title;
  let message;
  if (inGrace) {
    title = "Assinatura em período de carência";
    message = `Sua assinatura do plano ${planName} expirou e está em período de carência. Renove agora para não perder o acesso aos recursos.`;
  } else if (days <= 0) {
    title = "Sua assinatura expira hoje";
    message = `Sua assinatura do plano ${planName} expira hoje. Renove para manter o acesso sem interrupções.`;
  } else if (days <= 1) {
    title = "Sua assinatura expira amanhã";
    message = `Sua assinatura do plano ${planName} expira amanhã. Renove para manter o acesso sem interrupções.`;
  } else {
    title = "Sua assinatura está prestes a expirar";
    message = `Sua assinatura do plano ${planName} expira em ${days} dias. Renove para manter o acesso sem interrupções.`;
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      // Must sit above AnimatedModal (5000, or 9999 with forceOnTop). Below it,
      // this Dialog is hidden yet its focus trap still steals every keystroke
      // from the modal on top (e.g. "Valor Inicial" in CreateCashierModal).
      sx={{ zIndex: 10000 }}
      maxWidth="xs"
      fullWidth
      TransitionComponent={ZoomTransition}
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: "hidden",
          boxShadow: theme.shadows[10],
        },
      }}
    >
      {/* Accent strip mirrors the severity color */}
      <Box sx={{ height: 4, backgroundColor: accent }} />

      <DialogContent sx={{ pt: 4, pb: 2, px: 3 }}>
        <Stack alignItems="center" textAlign="center" spacing={2}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 72,
              height: 72,
              borderRadius: "50%",
              backgroundColor: alpha(accent, 0.12),
              color: accent,
            }}
          >
            <HeaderIcon sx={{ fontSize: 40 }} />
          </Box>

          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {title}
          </Typography>

          <Typography
            variant="body2"
            sx={{ color: theme.palette.text.secondary, lineHeight: 1.6 }}
          >
            {message}
          </Typography>

          {expiresLabel && (
            <Chip
              icon={<EventBusyRoundedIcon sx={{ fontSize: 18 }} />}
              label={
                inGrace
                  ? `Expirou em ${expiresLabel}`
                  : `Expira em ${expiresLabel}`
              }
              size="small"
              sx={{
                mt: 0.5,
                fontWeight: 600,
                color: accent,
                backgroundColor: alpha(accent, 0.1),
                border: `1px solid ${alpha(accent, 0.25)}`,
                "& .MuiChip-icon": { color: accent },
              }}
            />
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ flexDirection: "column", gap: 1, px: 3, pb: 3 }}>
        <Button
          fullWidth
          variant="contained"
          color={severity}
          onClick={onRenew}
          startIcon={<AutorenewRoundedIcon />}
          sx={{ textTransform: "none", fontWeight: 700, py: 1.1, borderRadius: 2 }}
        >
          Renovar assinatura
        </Button>
        <Button
          fullWidth
          variant="text"
          color="inherit"
          onClick={onClose}
          sx={{
            textTransform: "none",
            fontWeight: 500,
            color: theme.palette.text.secondary,
          }}
        >
          Agora não
        </Button>
      </DialogActions>
    </Dialog>
  );
}
